import {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import {Button, Spin, Table, Space, Popconfirm} from 'antd';

import type {NewProgram} from '@PikaDoApp/npm-pikado-logic/dist/modules/program/graphql/program.interface';
import {ProgramsNewService} from './ProgramsNewService';
import Title from 'antd/es/typography/Title';
import Text from 'antd/es/typography/Text';
import {resolveI18nObject} from '../../utils';
import {useTablePagination} from '../../hooks/useTablePagination';

const programsService = new ProgramsNewService();

export const ProgramsTable = () => {
  const [programs, setPrograms] = useState<any[]>([]);
  const [isError, setIsError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const {
    current,
    pageSize,
    total,
    setTotal,
    paginationConfig,
    paginationSelector,
  } = useTablePagination({itemName: 'programs', defaultPageSize: 100});

  useEffect(() => {
    setIsLoading(true);
    programsService
      .getNewPrograms()
      .then(resp => {
        setIsLoading(false);
        setPrograms(resp);
        setTotal(resp.length);
      })
      .catch(error => {
        setIsError(true);
        setIsLoading(false);
        //console.log('Error ->', error);
      });
  }, [current, pageSize]);

  const columns: any = [
    {
      title: 'Name',
      dataIndex: ['name'],
      render: (name: string, record: any) => {
        return <Link to={`/programs/${record.id}/edit`}>{record.name}</Link>;
      },
    },
    {
      title: 'Created By',
      dataIndex: 'createdBy',
    },
    {
      title: 'Deleted',
      dataIndex: 'isDeleted',
      render: (isDeleted: boolean) => (
        <Text type={isDeleted ? 'danger' : 'success'}>
          {isDeleted ? 'Yes' : 'No'}
        </Text>
      ),
    },
    {
      title: 'ID',
      dataIndex: 'id',
      sorter: (a: any, b: any) => a.id.localeCompare(b.id),
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (id: string) => <Link to={`/programs/${id}/edit`}>{id}</Link>,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <Popconfirm
          title="Delete this program?"
          onConfirm={() => {
            programsService.softDeleteNewProgram(record.id).then(() => {
              setPrograms(prev => prev.filter(p => p.id !== record.id));
              setTotal(prev => prev - 1);
            });
          }}>
          <Button danger type="link" style={{padding: 0}}>
            Delete
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          margin: 20,
        }}>
        <Title level={3} style={{margin: 0}}>
          Programs
          {!isLoading && total > 0 && (
            <Text
              type="secondary"
              style={{fontSize: 14, fontWeight: 400, marginLeft: 8}}>
              ({total} total)
            </Text>
          )}
        </Title>
        <Space>
          {paginationSelector}
          <Button type="primary" size={'large'}>
            <Link to={'/programs/create'} style={{color: 'inherit'}}>
              Create
            </Link>
          </Button>
        </Space>
      </div>
      {!isError && !isLoading && (
        <Table
          rowKey={record => record.id}
          pagination={paginationConfig}
          columns={columns}
          dataSource={programs}
        />
      )}
      {isLoading && (
        <Spin size="large" style={{margin: 'auto', width: '100%'}} />
      )}
      {isError && <Title>Programs fetching error ⚠️</Title>}
    </>
  );
};
